import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";
import { exchangeAppLockForJwt } from "@/lib/api";
import LanguageSelector from "@/components/LanguageSelector";

const KEY = "savvaspos_app_unlocked";
const USER = "savvaspos";
const PASS = "store";

/**
 * Global webpage-level access gate. Blocks the entire app behind a
 * centered modal until the operator enters the correct credentials.
 * Unlock state persists in localStorage so a refresh keeps the user in.
 *
 * Iter 38 — On successful unlock we ALSO exchange the device password
 * for a master-tenant JWT (stored under `kf_jwt`). This lights up every
 * JWT-protected endpoint (HARD RESET, /admin/*, /auth/me) without any
 * extra sign-in step for the operator.
 *
 * Iter 113 — Strict tenant isolation UX simplification. If the caller
 * already carries a JWT whose role is `admin` or `owner`, we skip the
 * AppLock gate entirely — those users authenticate directly via
 * /saas-login (JWT + backend role gate protect everything downstream).
 * Only the legacy MASTER kiosk staff flow (role=`master`) still needs
 * the shared iPad password.
 */
function decodeJwtRole(token) {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1] || ""));
    return String(payload.role || "").toLowerCase() || null;
  } catch {
    return null;
  }
}

function callerBypassesAppLock() {
  // Public routes handle their own gating — AppLock never wraps them, but
  // this helper is invoked from a route protected by AppLock, so we only
  // check the JWT role.
  const token =
    localStorage.getItem("kf_jwt") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    "";
  const role = decodeJwtRole(token);
  return role === "admin" || role === "owner";
}

export default function AppLock({ children }) {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(KEY) === "1" || callerBypassesAppLock(),
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const t = useT();

  // Iter 113 — Re-evaluate the bypass whenever the JWT changes (e.g.
  // right after /saas-login persists a new token). This lets a fresh
  // owner land straight into the ERP without ever seeing the AppLock.
  useEffect(() => {
    if (!unlocked && callerBypassesAppLock()) {
      setUnlocked(true);
    }
    const onStorage = () => {
      if (callerBypassesAppLock()) setUnlocked(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [unlocked]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const u = username.trim().toLowerCase();
    const p = password;
    if (u !== USER || p !== PASS) {
      setError(t("applock.error"));
      return;
    }
    // Local validation OK — unlock immediately so the UI is responsive.
    localStorage.setItem(KEY, "1");
    setUnlocked(true);
    setError("");
    // Best-effort silent JWT mint. If the backend exchange fails (e.g.
    // master row missing on a brand-new pod) we keep the user unlocked
    // anyway; the JWT-required pages will surface the error themselves.
    try {
      await exchangeAppLockForJwt(u, p);
    } catch {
      /* non-fatal — kiosk can still drive legacy MASTER endpoints */
    }
  };

  if (unlocked) return children;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/90 backdrop-blur-md"
      data-testid="app-lock-overlay"
    >
      {/* Language picker floats top-right inside the lock overlay so the
          operator can switch BEFORE seeing any localised labels. */}
      <div className="absolute top-5 right-5 z-10">
        <LanguageSelector variant="light" />
      </div>
      <form
        onSubmit={submit}
        className="w-[min(420px,92vw)] bg-white rounded-2xl shadow-2xl border border-slate-200 p-8 sm:p-10"
        data-testid="app-lock-form"
      >
        <div className="flex flex-col items-center text-center gap-2 mb-7">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
            <Lock className="h-7 w-7" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mt-2">
            {t("applock.title")}
          </h2>
          <p className="text-sm text-slate-500">
            {t("applock.subtitle")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label
              htmlFor="lock-user"
              className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold"
            >
              {t("applock.username")}
            </Label>
            <Input
              id="lock-user"
              data-testid="app-lock-username"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError("");
              }}
              className="mt-1.5 min-h-[48px] text-base border-2"
            />
          </div>

          <div>
            <Label
              htmlFor="lock-pass"
              className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold"
            >
              {t("applock.password")}
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="lock-pass"
                data-testid="app-lock-password"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                className="min-h-[48px] text-base border-2 pr-12"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-900"
                data-testid="app-lock-toggle-password"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="app-lock-error"
              role="alert"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="app-lock-submit"
            className="w-full min-h-[52px] bg-slate-900 hover:bg-slate-800 text-white font-bold text-base tracking-wide mt-2"
          >
            {t("applock.signIn")}
          </Button>
        </div>

        <p className="text-[11px] text-center text-slate-400 mt-6">
          {t("applock.footer")}
        </p>
      </form>
    </div>
  );
}
